import { Actions, CartItem } from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRouter } from 'next/router';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import get from 'lodash/get';
import _ from 'lodash';

import {
  GqlDispensaries,
  GqlIndividualFilteredProductQuery,
  GqlIndividualFilteredProductQueryVariables,
} from 'types/graphql';
import individualFilteredProduct from 'shared/graphql/product/individual-filtered-product.gql';

import { products as mockProducts } from 'src/tests';
import { useModals, ModalNames } from 'src/components/modals';
import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useGoToCheckout from 'hooks/use-go-to-checkout';
import useCart from 'src/hooks/use-cart';
import { cartItemId } from 'src/utils/helpers/product/product';
import { getVariables } from 'src/hooks/use-product';
import { parseProduct } from 'utils/helpers/product';
import useStores from 'shared/hooks/use-stores';
import ROUTES from 'src/routes';

type AdditionalRoutableParameters = {
  query?: Record<string, string>;
};

type GoToIdOrCnameParameters = AdditionalRoutableParameters & {
  cname?: string;
  id?: string;
};

type GoToSpecialParameters = AdditionalRoutableParameters & {
  id: string;
  type: 'offer' | 'sale';
};

export const useBridgeActions = (): Actions => {
  const { dispensary } = useDispensary() as { dispensary?: GqlDispensaries | undefined };
  const router = useRouter();
  const { openModal } = useModals();
  const { closeCartDrawer, dispensaryRootUrl, isKiosk, openCartDrawer } = useUI();
  const { apolloClient } = useStores();
  const Cart = useCart();

  const contextAwareRouterPush = ({ path = '', query = {}, contexts = ['store-front', 'kiosk'] }): void => {
    if (!contexts.includes(isKiosk ? 'kiosk' : 'store-front')) {
      return;
    }
    const rootRoute = `/${(dispensaryRootUrl as string | null) ?? ''}/${dispensary?.cName ?? ''}`;
    const pathname = path.startsWith('/') ? path : `/${path}`;
    void router.push({
      pathname: `${rootRoute}${pathname}`,
      query,
    });
  };

  const navigation = {
    goToBrand: ({ id, cname, query }: GoToIdOrCnameParameters) => {
      const identifier = cname ?? id;
      if (identifier) {
        contextAwareRouterPush({ path: `${ROUTES.BRANDS}/${identifier}`, query });
      }
    },

    goToBrandList: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.BRANDS, query: params?.query }),

    goToCategory: ({ id, cname, query }: GoToIdOrCnameParameters) => {
      const identifier = cname ?? id;
      if (identifier) {
        contextAwareRouterPush({ path: `${ROUTES.PRODUCTS}${identifier}`, query });
      }
    },

    goToCheckout: useGoToCheckout(`remote-extension`),

    goToCollection: ({ id, cname, query }: GoToIdOrCnameParameters) => {
      const identifier = cname ?? id;
      if (identifier) {
        contextAwareRouterPush({ path: `${ROUTES.COLLECTIONS}${identifier}`, query });
      }
    },
    goToInfoPage: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.INFO, query: params?.query }),

    goToLogin: () => openModal(ModalNames.login),

    goToLoyalty: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.USER_LOYALTY, query: params?.query }),

    goToReferral: (params?: AdditionalRoutableParameters & { referralCode?: string }) => {
      const referralCode = params?.referralCode;
      if (referralCode) {
        contextAwareRouterPush({ path: `/referral/${referralCode}`, query: params.query });
      }
    },

    goToProductDetails: ({ id, cname, query }: GoToIdOrCnameParameters) => {
      const identifier = cname ?? id;
      if (identifier) {
        contextAwareRouterPush({ path: `${ROUTES.PRODUCT}${identifier}`, query });
      }
    },

    goToProductList: ({
      brandCname,
      brandId,
      categoryCname,
      categoryId,
      query,
    }: {
      brandCname?: string;
      brandId?: string;
      categoryCname?: string;
      categoryId?: string;
      collectionCname?: string;
      collectionId?: string;
      query?: Record<string, string>;
    }) => {
      const categoryIdentifier = categoryCname ?? categoryId;
      const brandIdentifier = brandCname ?? brandId;
      if (categoryIdentifier) {
        contextAwareRouterPush({ path: `${ROUTES.PRODUCTS}${categoryIdentifier}`, query });
      } else if (brandIdentifier) {
        contextAwareRouterPush({ path: `${ROUTES.PRODUCTS}?brands=${brandIdentifier}`, query });
      }
    },

    goToRegister: () => openModal(ModalNames.signUp),

    goToSearch: (searchQuery: string | undefined, params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.PRODUCTS, query: { ...params?.query, search: searchQuery ?? '' } }),

    goToStore: ({ cname, id, query }: GoToIdOrCnameParameters) => {
      const identifier = cname ?? id;
      if (identifier) {
        contextAwareRouterPush({ path: `/stores/${identifier}`, contexts: ['store-front'], query });
      }
    },

    goToStoreBrowser: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: '/locations/browse', contexts: ['store-front'], query: params?.query }),

    goToStoreFront: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: '', contexts: ['store-front'], query: params?.query }),

    goToStoreLocator: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.LOCATIONS, contexts: ['store-front'], query: params?.query }),

    goToSpecialsList: (params?: AdditionalRoutableParameters) =>
      contextAwareRouterPush({ path: ROUTES.SPECIALS, query: params?.query }),

    goToSpecial: ({ id, type, query }: GoToSpecialParameters) => {
      const route = type === 'sale' ? ROUTES.SALE : ROUTES.OFFER;
      contextAwareRouterPush({ path: `${ROUTES.SPECIALS}${route}/${id}`, query });
    },
  };

  const cart = {
    addToCart: async (item: CartItem) => {
      const product = await getProduct({ apolloClient, dispensary, productId: item.productId });
      Cart.addItem(dispensary, { ...item, product });
    },
    clearCart: () => Cart.clearOrder(),
    hideCart: closeCartDrawer,
    removeFromCart: (item: CartItem) => Cart.removeItem(cartItemToId(item)),
    showCart: openCartDrawer,
    updateCartItem: (item: CartItem) => Cart.updateProduct(cartItemToId(item), item),
    updatePricingType: (pricingType: 'med' | 'rec') => Cart.changePricingType(pricingType),
  };

  return { ...cart, ...navigation };
};

const cartItemToId = (item: CartItem): string =>
  cartItemId({
    additionalOption: item.additionalOption,
    option: item.option,
    product: {
      additionalOptions: item.additionalOption ? [item.additionalOption] : [],
      id: item.productId,
    },
  });

const getProduct = async ({
  apolloClient,
  dispensary,
  productId,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject>;
  dispensary: GqlDispensaries | undefined;
  productId: string;
}): Promise<any> => {
  const { data } = await apolloClient.query<
    GqlIndividualFilteredProductQuery,
    GqlIndividualFilteredProductQueryVariables
  >({
    query: individualFilteredProduct,
    variables: getVariables(productId, dispensary),
  });

  return parseProduct(get(data, 'filteredProducts.products[0]', _.find(mockProducts, { id: productId })));
};
